import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faSearch,
  faTable, // Changed to faTable for a general view of records
  faInfoCircle,
  faFileArchive, // More specific icon for archived data
} from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from "../App";

const ArchivedData = () => {
  const [year, setYear] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const token = localStorage.getItem("token");
  const PropertyId = localStorage.getItem("selectedPropertyId");

  const fetchRecords = async () => {
    setRecords([]);
    setFetchError(null);
    setShowResults(false);

    if (!year.match(/^\d{4}$/)) {
      toast.error("Please enter a valid 4-digit year.");
      return;
    }

    if (!token || !PropertyId) {
      setFetchError("Authentication token or property ID missing. Please log in and select a property.");
      toast.error("Authentication token or property ID missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/viewrecordsrentlandlordyearselect/${PropertyId}`,
        { year },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setRecords(Array.isArray(response.data.response) ? response.data.response : []);
      setShowResults(true);
      if (Array.isArray(response.data.response) && response.data.response.length === 0) {
        toast.info(`No archived rent records found for the year ${year}.`);
      } else {
        toast.success("Archived rent records fetched successfully!");
      }
    } catch (error) {
      console.error("Error fetching records:", error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
        setFetchError(`No records found for the year ${year}.`);
        toast.info(`No records found for the year ${year}.`);
      } else {
        setFetchError(error.response?.data?.response || "Failed to fetch records.");
        toast.error(error.response?.data?.response || "Failed to fetch records.");
      }
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          
          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4 sm:mb-0">
                <FontAwesomeIcon icon={faFileArchive} className="mr-3 text-purple-600" />
                Archived Rent Records
              </h1>
            </div>

            {/* Year Input and Search Button */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8 p-6 bg-purple-50 rounded-lg shadow-inner">
              <div className="flex-grow">
                <label htmlFor="yearInput" className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-purple-500" />
                  Select Year
                </label>
                <input
                  id="yearInput"
                  type="number"
                  maxLength={4}
                  placeholder="YYYY"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                />
              </div>
              <button
                onClick={fetchRecords}
                disabled={loading}
                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Oval height={20} width={20} color="#FFF" secondaryColor="#C4B5FD" strokeWidth={5} strokeWidthSecondary={5} />
                ) : (
                  <FontAwesomeIcon icon={faSearch} className="mr-2" />
                )}
                {loading ? "Searching..." : "View Records"}
              </button>
            </div>

            {loading && !showResults && (
              <div className="flex items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#6D28D9"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='oval-loading'
                  secondaryColor="#C4B5FD"
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700">Fetching archived records...</p>
              </div>
            )}

            {fetchError && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error!</p>
                <p className="text-md">{fetchError}</p>
                {!showResults && (
                  <button
                    onClick={fetchRecords}
                    className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                  >
                    Try Again
                  </button>
                )}
              </div>
            )}

            {showResults && !loading && (
              records.length > 0 ? (
                <div className="overflow-x-auto mt-8">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    <FontAwesomeIcon icon={faTable} className="mr-2 text-purple-600" />
                    Records for {year} ({records.length})
                  </h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md">
                    <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Record ID
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          House Number
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Water Bill
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Rent Bill
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Trash Bill
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Service Bill
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Total Amount Paid
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {records.map((item, index) => (
                        <tr
                          key={item.record_id || index}
                          className={index % 2 === 0 ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-100"}
                        >
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.record_id || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.house_number || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-medium">
                            KES {parseFloat(item.water_bill || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-medium">
                            KES {parseFloat(item.rent_bill || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-medium">
                            KES {parseFloat(item.trash_bill || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-medium">
                            KES {parseFloat(item.service_bill || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                            KES {parseFloat(item.total_amount_paid || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-600 text-sm">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">No archived records found for the selected year.</p>
                  <p className="text-md text-gray-500 mt-2">Please select a different year or add new records.</p>
                </div>
              )
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default ArchivedData;